<template>
    <div class="container-fluid">
        <div v-if="form.WorkOrderId" class="alert alert-info d-flex align-items-center mb-3 py-2">
            <i class="fas fa-lock me-2"></i>
            <span>This item is linked to <a href="javascript:void(0);" @click="$router.push('/invoices/edit/' + form.WorkOrderId)"><strong>Invoice {{ form.WorkOrder?.InvoiceNumber || '#' + form.WorkOrderId }}</strong></a> and cannot be edited.</span>
        </div>
        <fieldset :disabled="!!form.WorkOrderId">
        <!-- Part Search -->
        <part-search-ac @select="onPartSelected"></part-search-ac>

        <!-- Row 1: Client, Request (WorkOrder), & Job -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Client</span>
                    <client-ac 
                        v-model="form.CustomerNumber" 
                        inputclass="form-control form-control-sm" 
                        placeholder="Search for a client..."
                    ></client-ac>
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Request</span>
                    <job-ac 
                        v-model="form.RequestId" 
                        v-model:work-order-id="form.InitialRequest"
                        :client="form.CustomerNumber"
                        :pm-only="pmOnly"
                        placeholder="Search for a request..."
                    ></job-ac>
                    <div class="form-text">Auto-populated from chosen client.</div>
                </label>
            </div>
        </div>

        <!-- Row 2: Assigned To & Order Status -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Assigned To</span>
                    <tech-ac 
                        v-model="form.AssignedTo" 
                        placeholder="Search for a technician..."
                    ></tech-ac>
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <kt-select
                    :options="status"
                    label="Order Status"
                    placeholder="Select Order Status"
                    v-model="form.OrderStatus"
                    inputclass="form-control form-control-sm"
                ></kt-select>
            </div>
        </div>

        <!-- Row 3: Description (full width) -->
        <div class="row">
            <div class="col-12 mb-3">
                <label class="d-block">
                    <div class="d-flex justify-content-between">
                        <span class="form-label fw-bold">Description</span>
                        <span @click="showFormat();"><i class="fas fa-edit"></i></span>
                    </div>
                    <textarea 
                        class="form-control form-control-sm" 
                        rows="4" 
                        v-model="form.PartDescription"
                        placeholder="Part Description"
                    ></textarea>
                </label>
            </div>
        </div>

        <!-- Row 4: Part Number & Link Reference -->
        <div class="row">
            <div class="col-md-8 mb-3">
                <label class="d-block">
                    <span class="form-label">Part Number</span>
                    <input 
                        type="text" 
                        class="form-control form-control-sm" 
                        v-model="form.PartNumber" 
                        placeholder="Part Number"
                    >
                </label>
            </div>
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">Quantity</span>
                    <input 
                        type="number" 
                        class="form-control form-control-sm" 
                        v-model="form.Quantity" 
                        placeholder="Quantity"
                    >
                </label>
            </div>
        </div>

        <!-- Row 5: Pricing -->
        <div class="row">
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">MSRP</span>
                    <input 
                        type="number" 
                        step="0.01" 
                        class="form-control form-control-sm" 
                        v-model="form.MSRP" 
                        placeholder="0.00"
                    >
                </label>
            </div>
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">Cost</span>
                    <input 
                        type="number" 
                        step="0.01" 
                        class="form-control form-control-sm" 
                        v-model="form.PartsCost" 
                        placeholder="0.00"
                    >
                </label>
            </div>
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">Needed By</span>
                    <input 
                        type="date" 
                        class="form-control form-control-sm" 
                        v-model="form.NeededBy"
                    >
                </label>
            </div>
        </div>

        <!-- Row 6: Order Details -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Order Number</span>
                    <input 
                        type="text" 
                        class="form-control form-control-sm" 
                        v-model="form.OrderNumber" 
                        placeholder="Order Number"
                    >
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <kt-select
                    :options="payments"
                    label="Payment Method"
                    placeholder="Select Payment Method"
                    v-model="form.PaymentMethod"
                    inputclass="form-control form-control-sm"
                ></kt-select>
            </div>
        </div>

        <!-- Row 7: Link Reference & Work Complete Date -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Link Reference</span>
                    <input 
                        type="text" 
                        class="form-control form-control-sm" 
                        v-model="form.LinkReference" 
                        placeholder="Part Link/URL"
                    >
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Work Complete Date</span>
                    <input 
                        type="date" 
                        class="form-control form-control-sm" 
                        v-model="form.WorkCompleteDate"
                    >
                </label>
            </div>
        </div>

        <div class="row" v-if="form.Id">
            <div class="col">
                <attachment-form
                    :part="form.Id"
                    :request="form.RequestId"
                    label="Receipts / Attachments"
                ></attachment-form>
            </div>
        </div>
        </fieldset>
    </div>

    <kt-modal
        title="Description Format"
        :visible="formatShow"
        @dismiss="formatShow = false;"
    >
        <template #body>
            <kt-input
                class="mb-2"
                label="Date"
                type="date"
                v-model="format.Date"
            ></kt-input>

            <kt-input
                class="mb-2"
                label="Vendor Name"
                placeholder="Part Vendor Name"
                v-model="format.VendorName"
            ></kt-input>

            <kt-input
                class="mb-2"
                label="Order #"
                placeholder="Order Number"
                v-model="format.OrderNum"
            ></kt-input>

            <kt-input
                class="mb-2"
                label="Receipt / Invoice #"
                placeholder="Receipt or Invoice Number"
                v-model="format.ReceiptNum"
            ></kt-input>

            <kt-input
                class="mb-2"
                label="Job Id"
                placeholder="Job ID"
                v-model="format.JobId"
            ></kt-input>

            <kt-input
                label="Dollar Amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                v-model="format.Amount"
            ></kt-input>
        </template>

        <template #footer>

            <div class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-secondary" @click="formatShow = false;">
                    <i class="fa fa-x"></i> &nbsp; Cancel
                </button>
                <button class="btn btn-sm btn-outline-primary" @click="applyFormat">
                    <i class="fa fa-check"></i> &nbsp; OK
                </button>
            </div>

        </template>

    </kt-modal>

</template>

<script setup>

import { ref, onMounted, computed } from "vue";
import { PartModel } from "@/models/PartModel";
import Helper, { DateFormat } from "@/services/Helper";
import { storeToRefs } from "pinia";
import { useOrderStatusStore } from "@/stores/OrderStatusStore";
import KtModal from "@/components/UI/KtModal.vue";
import KtSelect from "@/components/UI/KtSelect.vue";
import KtInput from "@/components/UI/KtInput.vue";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import TechAc from "@/components/Techs/TechAc.vue";
import PartSearchAc from "@/components/Parts/PartSearchAc.vue";

import AttachmentForm from "@/components/Attachments/AttachmentForm.vue";

const format = ref({});
const formatShow = ref(false);

// Use OrderStatusStore instead of static Attic.js
const orderStatusStore = useOrderStatusStore();
const { list: orderStatusList } = storeToRefs(orderStatusStore);

const status = computed(() => 
    Object.values(orderStatusList.value).map(s => ({
        value: s.Id,
        label: s.Description
    }))
);

// Load order statuses on mount
onMounted(async () => {
    await orderStatusStore.load();
});

// Note: Payments list doesn't have a store yet, keeping static reference
const payments = ref([
    { value: "1", label: "Cash" },
    { value: "2", label: "BB&T debit card" },
    { value: "3", label: "Elan credit card" },
    { value: "4", label: "Check" },
    { value: "5", label: "Checking Account" },
    { value: "6", label: "Vendor Credit Line" },
    { value: "7", label: "Part from Inventory" },
    { value: "8", label: "EE Credit Card" },
    { value: "9", label: "JP CC" },
    { value: "10", label: "EV CC" },
    { value: "11", label: "RS CC" },
    { value: "12", label: "DK CC" },
    { value: "13", label: "Petty Cash" },
    { value: "14", label: "CL CC" },
    { value: "15", label: "AV CC" },
    { value: "16", label: "JF CC" }
]);

const props = defineProps({
    pmOnly: {
        type: Boolean,
        default: false
    }
});

const form = defineModel(
{
    type: PartModel,
    default: new PartModel()
});

const showFormat = () => {
    // Initialize format with defaults
    const today = new Date();
    format.value = {
        Date: Helper.dateToInput(today, DateFormat.Date),
        VendorName: '',
        OrderNum: '',
        ReceiptNum: '',
        JobId: form.value.RequestId?.toString() || '',
        Amount: ''
    };
    formatShow.value = true;
}

const applyFormat = () => {
    // Build description from format data
    let description = '';
    
    if (format.value.Date) description += format.value.Date + ' ';
    if (format.value.VendorName) description += format.value.VendorName + ' ';
    if (format.value.OrderNum) description += format.value.OrderNum + ' ';
    if (format.value.ReceiptNum) description += format.value.ReceiptNum + ' ';
    
    // Add dash separator before job id if we have content
    if (description && format.value.JobId) {
        description = description.trim() + ' - ' + format.value.JobId;
    } else if (format.value.JobId) {
        description += format.value.JobId;
    }
    
    // Add dash separator before amount if we have content
    if (description && format.value.Amount) {
        description += ' - ' + format.value.Amount;
    } else if (format.value.Amount) {
        description += format.value.Amount;
    }
    
    // Set the description and store format data for file naming
    form.value.PartDescription = description.trim();
    form.value.FormatData = JSON.stringify(format.value);
    
    formatShow.value = false;
}

const onPartSelected = (part) => {
    // Auto-populate form fields from selected part
    form.value.PartDescription = part.PartDescription;
    form.value.PartNumber = part.PartNumber;
    form.value.MSRP = part.MSRP;
    form.value.PartsCost = part.PartsCost;
    form.value.Quantity = part.Quantity || 1;
    form.value.LinkReference = part.LinkReference;
    
    // Set NeededBy and WorkCompleteDate to today
    const today = new Date();
    form.value.NeededBy = Helper.dateToInput(today, DateFormat.Date);
    form.value.WorkCompleteDate = Helper.dateToInput(today, DateFormat.Date);
};

</script>